import { FileText, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';

import { GetInvoicesListInitialData } from './api/IncomingInvoice.api';
import IncomeInvoice from './components/Invoice';
import IncomeInvoicesList from './components/InvoicesList';

// Define the two stages
type Stage = 'search' | 'view';

function IncomeInvoicesPage() {
  //--state for filters
  const [startDate, setStartDate] = useState<string>(
    dayjs().subtract(7, 'day').format('MM-DD-YYYY'),
  );
  const [endDate, setEndDate] = useState<string>(dayjs().format('MM-DD-YYYY'));
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<number | null>(null);

  //--stage management
  const [currentStage, setCurrentStage] = useState<Stage>('search');

  //-- inital Query
  const initialData = useQuery({
    queryKey: ['IncomeInvoicesList', startDate, endDate, supplierId, invoiceId],
    queryFn: () =>
      GetInvoicesListInitialData({
        startDate: dayjs(startDate).format('DD-MM-YYYY'),
        endDate: dayjs(endDate).format('DD-MM-YYYY'),
        supplierId,
        invoiceId,
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,

    retryDelay(failureCount) {
      if (failureCount > 0) {
        toast.error(
          '❌ فلتر لا يحتوي علي فواتير 📄 او رقم فاتورة غير صحيحة ⚠️',
        );
        resetFilters();
      }
      return 200;
    },
  });

  //-- REset filters
  const resetFilters = () => {
    setStartDate(dayjs().subtract(7, 'day').format('MM-DD-YYYY'));
    setEndDate(dayjs().format('MM-DD-YYYY'));
    setSupplierId(null);
    setInvoiceId(null);
  };

  //-- Handle invoice selection and stage transition
  const handleInvoiceSelect = (invoiceId: number) => {
    setSelectedInvoice(invoiceId);
    setCurrentStage('view');
  };

  //-- Handle return to search stage
  const handleBackToSearch = () => {
    setSelectedInvoice(null);
    setCurrentStage('search');
  };

  return (
    <>
      <Helmet>
        <title> فواتير الشراء </title>
      </Helmet>

      {/* Breadcrumb Navigation */}
      <div className="mb-4 px-4 pt-4">
        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            <span>البحث والاختيار</span>
          </div>
          {currentStage === 'view' && (
            <>
              <div className="h-1 w-4 rounded-full bg-slate-300 dark:bg-slate-600"></div>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>عرض التفاصيل</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="transition-all duration-300 ease-in-out">
        {currentStage === 'search' ? (
          // First Stage: Search and Select Invoices
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800">
            <IncomeInvoicesList
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              invoicesList={initialData?.data?.data?.invoices}
              isLoading={initialData?.isLoading}
              invoiceId={invoiceId}
              setInvoiceId={setInvoiceId}
              resetFilters={resetFilters}
              setSelectedInvoice={handleInvoiceSelect}
              selectedInvoice={selectedInvoice}
            />
          </div>
        ) : (
          // Second Stage: View Selected Invoice
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800">
            <IncomeInvoice
              selectedInvoice={selectedInvoice}
              setSelectedInvoice={setSelectedInvoice}
              onBackToSearch={handleBackToSearch}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default IncomeInvoicesPage;
